import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { chat, close } from '@/assets/icons';
import { AsaphIconButton } from '@/components/shared/Button';
import { NotificationBadge } from '@/components/shared/NotificationBadge';
import { LinkFrom, Video } from '@/config/constants';
import {
	authorProfileUrl,
	dashboardUrl,
	directMessagesUrl,
	libraryUrl,
} from '@/config/urls';
import { GetVideoQuery, useUpsertVideoStatMutation } from '@/graphql';
import { getAdjacentVideos } from '@/helpers/getAdjacentVideos';
import { EThemePaletteTypes } from '@/hooks/useDarkMode';
import { useMediaDomainUrl } from '@/hooks/useMediaDomainUrl';
import { useStatusBar } from '@/hooks/useStatusBar';
import { useThrottle } from '@/hooks/useThrottle';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useSubscription } from '@/providers/SubscriptionProvider';
import { useTeam } from '@/providers/TeamProvider';
import { useUnreadChats } from '@/providers/UnreadChatsProvider/UnreadChatsProvider';
import constants from '@/theme/exportedVariables.module.scss';
import { CircularProgress, SvgIcon, useMediaQuery } from '@material-ui/core';
import { isNil } from 'ramda';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { ChatVideo } from '../ChatVideo';
import { IconButton } from '../IconButton';
import { InfoArea } from '../InfoArea';
import { Player } from '../Player/Player';
import { PlayerHeader } from '../PlayerHeader';
import styles from './VideoPlayerContent.module.scss';

interface IPropsVideoPlayerContent {
	video: GetVideoQuery['getVideo'];
	refetchVideo: () => Promise<any>;
	loading: boolean;
	originalPalette?: EThemePaletteTypes;
	error?: any;
}

interface VideoPlayerLocationProps {
	linkFrom: LinkFrom;
	shouldChatBeOpened: boolean;
}

export const VideoPlayerContent: React.FC<IPropsVideoPlayerContent> = ({
	video,
	refetchVideo,
	loading,
	originalPalette,
	error,
}) => {
	// const [videoPoster, setVideoPoster] = useState<string | null>(null);
	// const [videoUrl, setVideoUrl] = useState<string | null>(null);
	const { videoChatIds } = useUnreadChats();
	const [chatOpen, setChatOpen] = useState<boolean>(false);
	const [chatExpanded, setChatExpanded] = useState<boolean>(false);
	const closeChat = useCallback(() => {
		setChatOpen(false);
		setChatExpanded(false);
	}, []);
	const [videoMilestone, setVideoMilestone] = useState<
		0 | 1 | 25 | 50 | 75 | 100
	>(0);
	const fireVideoProgressEvent = useAnalyticsEvent('Video_Progress');

	const videoUrl = useMediaDomainUrl(video.url ?? '') || null;
	const hasNotification =
		!!video.chat?.id && videoChatIds.includes(video.chat.id);
	// const { loading: LoadingSignature, signedQueryParams } = useSignedUrlVideo(
	// 	video ? video.id : null,
	// );

	const displayLeftColumn = useMediaQuery(
		`(min-width: ${constants.sizeSmallDesktop})`,
	);
	const displayRightColumn = useMediaQuery(
		`(min-width: ${constants.sizeSmallDesktop})`,
	);

	const [synopsisShown, setSynopsisShown] = useState<boolean>(false);
	const [devotionTextShown, setDevotionTextShown] = useState<boolean>(false);
	const [upNextShown, setUpNextShown] = useState<boolean>(false);

	const location = useLocation();
	const locationState = location.state as VideoPlayerLocationProps;

	const { status: subscriptionStatus } = useSubscription();
	const { mutateAsync: upsertVideoStatMutate } = useUpsertVideoStatMutation();

	// useEffect(() => {
	// 	if (video?.type === 'devotion') {
	// 		retry();
	// 	}
	// }, [video?.type, retry]);

	useEffect(() => {
		if (!loading && !displayRightColumn) {
			setChatOpen(!!locationState?.shouldChatBeOpened);
		}
	}, [locationState?.shouldChatBeOpened, displayRightColumn, loading]);

	useEffect(() => {
		if (videoMilestone > 0) {
			fireVideoProgressEvent({
				name: video.name,
				progress: videoMilestone,
			});
		}
	}, [fireVideoProgressEvent, videoMilestone, video]);

	// const updateTyping = useUpdateTyping(video?.id);
	const { disableVideoMode } = useStatusBar();

	const navigate = useNavigate();
	const handleVideoClose = useCallback(() => {
		// updateTyping();
		// Revert to light mode
		if (originalPalette === EThemePaletteTypes.LIGHT) {
			disableVideoMode();
		}

		if (locationState?.linkFrom === LinkFrom.messages) {
			return navigate(directMessagesUrl);
		} else if (locationState?.linkFrom === LinkFrom.library) {
			return navigate(libraryUrl);
		} else if (
			locationState?.linkFrom.includes(
				authorProfileUrl.replace(':contributorId', ''),
			)
		) {
			return navigate(locationState?.linkFrom);
		}
		return navigate(dashboardUrl);
	}, [
		locationState?.linkFrom,
		navigate,
		// updateTyping,
		originalPalette,
		disableVideoMode,
	]);

	const updatePercentagePlayed = useCallback(
		(percentagePlayed: number) => {
			upsertVideoStatMutate({
				videoId: video.id,
				input: { percentagePlayed },
			});

			switch (true) {
				case percentagePlayed > 0.9:
					setVideoMilestone(100);
					break;
				case percentagePlayed > 0.74:
					setVideoMilestone(75);
					break;
				case percentagePlayed > 0.49:
					setVideoMilestone(50);
					break;
				case percentagePlayed > 0.24:
					setVideoMilestone(25);
					break;
				case percentagePlayed > 0:
					setVideoMilestone(1);
					break;
			}
		},
		[upsertVideoStatMutate, video.id],
	);

	const updatePercentagePlayedThrottled = useThrottle(
		updatePercentagePlayed,
		Video.STATS_UPDATE_THROTTLE_MS,
	);

	// 1 is to exclude yourself
	// const hasTeam =
	// 	(team?.members.length ?? 0) > 1 || (team?.invited.length ?? 0) > 0;
	// const isDevotion = video?.type === 'devotion';
	// const isDevotion = false;
	const { prevVideo, nextVideo } = getAdjacentVideos(video);
	const isChatVisible =
		displayRightColumn || (!displayRightColumn && chatOpen);

	const classes = [
		styles['video-player'],
		chatExpanded ? styles['video-player--expanded'] : '',
	];

	if (displayLeftColumn) {
		classes.push(styles['video-player--with3columns']);
	}
	if (!displayLeftColumn && displayRightColumn) {
		classes.push(styles['video-player--with2columns']);
	}
	if (isChatVisible) {
		classes.push(styles['video-player--withChatOpen']);
	}

	return (
		<div className={classes.join(' ')}>
			<div className={styles['video-player__info-area']}>
				{displayLeftColumn && (
					<InfoArea
						onCloseClick={handleVideoClose}
						pageLoading={loading}
						// isDevotion={isDevotion}
						video={video}
						// devotionTextShown={devotionTextShown}
						// setDevotionTextShown={setDevotionTextShown}
					/>
				)}
			</div>

			<div className={styles['video-player__player-area']}>
				{!displayLeftColumn && displayRightColumn && (
					<div className={styles['video-player__player-area-close']}>
						<IconButton
							icon={close}
							color="transparent"
							onClick={handleVideoClose}
							size="medium"
						/>
					</div>
				)}
				{/* {loading ? (
					<div className={styles['video-player__loading']}>
						<CircularProgress />
					</div>
				) :  */}
				{/* {noVideo ? (
					<Navigate to={directMessagesUrl} />
				) : */}
				{error ? (
					<div className={styles['video-player__error']}>Error</div>
				) : (
					<Player
						videoId={video.id}
						videoUrl={videoUrl}
						percentagePlayed={video.stat?.percentagePlayed ?? 0}
						startVideo={
							(video.stat?.percentagePlayed ?? 0) * video.duration
						}
						posterUrl={video.posterUrl}
						// signedQueryParams={signedQueryParams}
						headerElement={
							<PlayerHeader
								video={video}
								synopsisShown={synopsisShown}
								onClick={() => setSynopsisShown(!synopsisShown)}
								onCloseClick={handleVideoClose}
								chatOpen={chatOpen}
								// isDevotion={isDevotion}
								upNextShown={upNextShown}
								setUpNextShown={setUpNextShown}
								displayRightColumn={displayRightColumn}
							/>
						}
						footerElement={
							chatOpen ? undefined : (
								<>
									{/* <div
								 		className={
								 			styles[
								 				'video-player__devotion-text-toggle'
								 			]
								 		}
								 	>
								 		{isDevotion && (
								 			<DevotionTextToggle
								 				devotionTextShown={
								 					devotionTextShown
								 				}
								 				setDevotionTextShown={
								 					setDevotionTextShown
								 				}
								 			/>
								 		)}
								 	</div> */}
									{!displayRightColumn && (
										<div
											className={
												styles['video-player__footer']
											}
										>
											<NotificationBadge
												borderless
												type="notification"
												verticalPosition="top"
												horizontalPosition="right"
												overlap="circular"
												invisible={!hasNotification}
											>
												<AsaphIconButton
													aria-label="open chat"
													color="grey"
													shade="light"
													size="large"
													onClick={() =>
														setChatOpen(true)
													}
													icon={
														<SvgIcon
															component={chat}
														/>
													}
												/>
											</NotificationBadge>
										</div>
									)}
								</>
							)
						}
						onPercentagePlayedChange={
							updatePercentagePlayedThrottled
						}
						synopsisShown={synopsisShown}
						displayLeftColumn={displayLeftColumn}
						displayRightColumn={displayRightColumn}
						chatOpen={chatOpen}
						chatExpanded={chatExpanded}
						// isDevotion={isDevotion}
						// devotionTextShown={devotionTextShown}
						// devotionText={pathOr(
						// 	undefined,
						// 	['devotionText'],
						// 	video,
						// )}
						// devotionTextFooter={pathOr(
						// 	undefined,
						// 	['devotionTextFooter'],
						// 	video,
						// )}
						upNextShown={upNextShown}
						setUpNextShown={setUpNextShown}
						prevVideo={prevVideo}
						nextVideo={nextVideo}
						onCloseClick={handleVideoClose}
						isPaidContent={subscriptionStatus ? false : video.paid}
						linkFrom={locationState?.linkFrom}
					/>
				)}
			</div>
			{isChatVisible && (
				<div className={styles['video-player__chat-area']}>
					<ChatVideo
						videoId={video.id}
						refetchVideo={refetchVideo}
						chat={video.chat}
						closeable={!displayRightColumn}
						expanded={chatExpanded}
						onExpand={() =>
							setChatExpanded((prevState) => !prevState)
						}
						onClose={closeChat}
					/>
				</div>
			)}
		</div>
	);
};
